// Configuração dos tipos de lead e suas colunas esperadas

export interface TipoLeadConfig {
  label: string;
  emoji: string;
  cor: string;
  colunasObrigatorias: string[];
  colunasOpcionais: string[];
  mapeamento: Record<string, string[]>; // campo_db: [possíveis nomes na planilha]
}

// Planilha TT: as colunas de linha movel vao de "Movel 1" ate "Movel 18"
// (as bases de maio/junho de 2026 chegaram a 18 linhas na mesma conta)
export const MAX_LINHAS_MOVEL = 18;

export type GrupoTT = 'COM_MOVEL' | 'SEM_MOVEL';

/**
 * A planilha TT vem separada em abas "MOVEL" e "SEM MOVEL" (as vezes "COM TEL"/
 * "SEM TEL", as vezes so "Planilha1"/"Planilha2"). Cada uma alimenta uma campanha
 * diferente. Decidimos pelo CONTEUDO — se a linha tem numero movel — porque o nome
 * da aba nao e' confiavel; conferido nas 18 bases de 2026, a classificacao por
 * conteudo bate 100% com o nome quando o nome existe.
 */
export function classificarGrupoTT(row: Record<string, unknown>): GrupoTT {
  for (let i = 1; i <= MAX_LINHAS_MOVEL; i++) {
    const temLinha = [`Movel ${i}`, `MOVEL_${i}`, `movel_${i}`, `MOVEL${i}`, `Móvel ${i}`]
      .some(c => row[c] !== undefined && row[c] !== null && String(row[c]).trim() !== '');
    if (temLinha) return 'COM_MOVEL';
  }
  return 'SEM_MOVEL';
}

export const TIPOS_LEAD_CONFIG: Record<string, TipoLeadConfig> = {
  // ⚡ Ecossistema API: tipo gerado automaticamente pela página /ecossistema-api
  // ao virar uma consulta (CNPJ enriquecido com Receita+Assertiva+Operadoras+
  // Cobertura) em lead. NÃO é importado por planilha — não precisa de mapeamento
  // de colunas; entradas mínimas só para o tipo aparecer em filtros, badges e
  // gráfico de Mix por Tipo de Lead.
  ECOSSISTEMA_API: {
    label: 'Ecossistema API',
    emoji: '⚡',
    cor: '#6366f1', // indigo (combina com a sidebar do Ecossistema API)
    colunasObrigatorias: ['CNPJ'],
    colunasOpcionais: [],
    mapeamento: {
      cnpj: ['CNPJ', 'cnpj'],
      razao_social: ['RAZAO_SOCIAL', 'razao_social', 'NOME', 'nome'],
    },
  },
  RENOVACAO_CLARO: {
    label: 'Renovação Claro',
    emoji: '🔴',
    cor: '#dc2626',
    colunasObrigatorias: ['CNPJ', 'RAZAO_SOCIAL', 'TEMPO_CONTRATO_VOZ', 'TEMPO_CONTRATO_BL'],
    colunasOpcionais: ['QTD_LINHAS_VOZ', 'QTD_LINHAS_BL', 'SEGMENTO', 'PRODUTO', 'DATA_LIMITE_RENOVACAO', 'CIDADE', 'UF', 'TELEFONE', 'CLASSIFICACAO', 'DDD', 'SOCIO_ADM', 'CARTEIRA', 'TERRITORIO', 'TEMPO_CONTRATO_TELEMETRIA', 'QTD_LINHAS_TELEMETRIA', 'CODIGO_CLARO', 'REGIONAL', 'DESCONTO_CLARO', 'GRUPO_HIERARQUIA'],
    mapeamento: {
      cnpj: ['CNPJ', 'cnpj', 'Cnpj', 'CPF_CNPJ', 'cpf_cnpj', 'NR_CNPJ', 'Nr_Cnpj', 'nr_cnpj'],
      razao_social: ['RAZAO_SOCIAL', 'razao_social', 'Razao Social', 'RAZÃO SOCIAL', 'Razão Social', 'Razão social', 'NOME', 'nome',
                     'NM_CLIENTE', 'Nm_Cliente', 'nm_cliente'],
      nome_fantasia: ['NOME_FANTASIA', 'nome_fantasia', 'Nome Fantasia', 'FANTASIA'],
      telefone: ['TELEFONE', 'telefone', 'Telefone', 'FONE', 'fone', 'TEL', 'tel', 'CELULAR', 'celular', 'TELECONE', 'Telecone', 'Telefone de Contato', 'TELEFONE DE CONTATO', 'telefone_de_contato'],
      email: ['EMAIL', 'email', 'Email', 'E-MAIL', 'e-mail'],
      cidade: ['CIDADE', 'cidade', 'Cidade', 'MUNICIPIO', 'municipio'],
      uf: ['UF', 'uf', 'ESTADO', 'estado', 'Estado'],
      // ✅ TODAS as variações possíveis de "Tempo de Contrato (Voz)"
      // ✅ Inclui detecção dinâmica de "MES EM JAN26", "MES EM FEV26", etc. (MAPA Vivo)
      tempo_contrato_voz: [
        'TEMPO_CONTRATO_VOZ', 'tempo_contrato_voz', 'TEMPO VOZ', 'Tempo Voz', 'MESES_VOZ',
        // Variações com parênteses e espaços
        'Tempo de contrato (Voz)', 'TEMPO DE CONTRATO (VOZ)', 'tempo de contrato (voz)',
        'Tempo de Contrato (Voz)', 'Tempo De Contrato (Voz)', 'TEMPO DE CONTRATO( VOZ )',
        'Tempo de contrato(Voz)', 'Tempo Contrato Voz', 'tempo_de_contrato_voz',
        'Meses Contrato Voz', 'MESES CONTRATO VOZ', 'TempoContratoVoz', 'Tempo contrato voz',
        // Outras variações
        'Tempo de Contrato Voz', 'TEMPO DE CONTRATO VOZ', 'Tempo De Contrato Voz',
        // MAPA Vivo — coluna dinâmica ex: "MES EM JAN26", "MES EM FEV26"
        'MES EM JAN26', 'MES EM FEV26', 'MES EM MAR26', 'MES EM ABR26', 'MES EM MAI26',
        'MES EM JUN26', 'MES EM JUL26', 'MES EM AGO26', 'MES EM SET26', 'MES EM OUT26',
        'MES EM NOV26', 'MES EM DEZ26',
        'MES EM JAN25', 'MES EM FEV25', 'MES EM MAR25', 'MES EM ABR25', 'MES EM MAI25',
        'MES EM JUN25', 'MES EM JUL25', 'MES EM AGO25', 'MES EM SET25', 'MES EM OUT25',
        'MES EM NOV25', 'MES EM DEZ25',
      ],
      // ✅ TODAS as variações de "Tempo de Contrato (Banda Larga)"
      tempo_contrato_bl: [
        'TEMPO_CONTRATO_BL', 'tempo_contrato_bl', 'TEMPO BL', 'Tempo BL', 'MESES_BL', 'TEMPO_DADOS',
        'Tempo de contrato (Banda Larga)', 'TEMPO DE CONTRATO (BANDA LARGA)', 'tempo de contrato (banda larga)',
        'Tempo de Contrato (Banda Larga)', 'Tempo De Contrato (Banda Larga)', 'Tempo Contrato BL',
        'Tempo de Contrato BL', 'TEMPO DE CONTRATO BL', 'Tempo contrato banda larga'
      ],
      // ✅ TELEMETRIA - Novos campos
      tempo_contrato_telemetria: [
        'TEMPO_CONTRATO_TELEMETRIA', 'tempo_contrato_telemetria', 'TEMPO TELEMETRIA', 'Tempo Telemetria',
        'Tempo de contrato (Telemetria)', 'TEMPO DE CONTRATO (TELEMETRIA)', 'tempo de contrato (telemetria)',
        'Tempo de Contrato (Telemetria)', 'Tempo De Contrato (Telemetria)', 'Tempo Contrato Telemetria',
        'MESES_TELEMETRIA', 'Meses Telemetria'
      ],
      // ✅ TODAS as variações de "Quantidade de linhas (Voz)"
      qtd_linhas_voz: [
        'QTD_LINHAS_VOZ', 'qtd_linhas_voz', 'LINHAS_VOZ', 'Linhas Voz', 'QTD_VOZ',
        'Quantidade de linhas (Voz)', 'QUANTIDADE DE LINHAS (VOZ)', 'quantidade de linhas (voz)',
        'Quantidade de Linhas (Voz)', 'Quantidade De Linhas (Voz)', 'Qtd Linhas Voz',
        'QTD LINHAS VOZ', 'QuantidadeLinhasVoz', 'Quantidade linhas voz'
      ],
      // ✅ TODAS as variações de "Quantidade de linhas (Banda Larga)"
      qtd_linhas_bl: [
        'QTD_LINHAS_BL', 'qtd_linhas_bl', 'LINHAS_BL', 'Linhas BL', 'QTD_BL', 'LINHAS_DADOS',
        'Quantidade de linhas (Banda Larga)', 'QUANTIDADE DE LINHAS (BANDA LARGA)', 'quantidade de linhas (banda larga)',
        'Quantidade de Linhas (Banda Larga)', 'Quantidade De Linhas (Banda Larga)', 'Qtd Linhas BL',
        'QTD LINHAS BL', 'QuantidadeLinhasBL', 'Quantidade linhas bl'
      ],
      // ✅ TELEMETRIA - Quantidade de linhas
      qtd_linhas_telemetria: [
        'QTD_LINHAS_TELEMETRIA', 'qtd_linhas_telemetria', 'LINHAS_TELEMETRIA', 'Linhas Telemetria',
        'Quantidade de linhas (Telemetria)', 'QUANTIDADE DE LINHAS (TELEMETRIA)', 'quantidade de linhas (telemetria)',
        'Quantidade de Linhas (Telemetria)', 'Quantidade De Linhas (Telemetria)', 'Qtd Linhas Telemetria',
        'QTD LINHAS TELEMETRIA', 'QuantidadeLinhasTelemetria', 'Quantidade linhas telemetria'
      ],
      segmento: ['SEGMENTO', 'segmento', 'Segmento', 'SEGMENT'],
      produto: ['PRODUTO', 'produto', 'Produto', 'PLANO', 'plano'],
      data_limite_renovacao: ['DATA_LIMITE_RENOVACAO', 'data_limite_renovacao', 'DATA_LIMITE', 'Data Limite', 'VENCIMENTO_CONTRATO'],
      classificacao: ['CLASSIFICACAO', 'classificacao', 'Classificação', 'Classificacao', 'CLASS', 'SEGMENTO_CLASSIFICACAO'],
      ddd: ['DDD', 'ddd', 'Ddd', 'CODIGO_AREA', 'COD_AREA'],
      nome_adm: ['SOCIO_ADM', 'socio_adm', 'Nome do ADM', 'NOME_ADM', 'Sócio ADM', 'SOCIO', 'ADMINISTRADOR', 'ADM', 'NOME ADM', 'Nome Adm', 'Administrador', 'Adm', 'CONTATO ADM', 'contato_adm'],
      carteira: ['CARTEIRA', 'carteira', 'Carteira', 'CONSULTOR', 'VENDEDOR', 'Nome do Membro de Equipe', 'NOME DO MEMBRO DE EQUIPE', 'Nome Membro Equipe', 'NOME_MEMBRO_EQUIPE', 'Nome do Membro de Equip'],
      territorio: ['TERRITORIO', 'Território', 'territorio', 'REGIAO', 'REGION', 'TERRITÓRIO'],
      // ✅ Novos campos Claro
      codigo_claro: ['Código', 'CODIGO', 'Codigo', 'CODIGO_CLARO', 'codigo_claro', 'COD', 'Cod'],
      regional: ['Regional', 'REGIONAL', 'regional', 'REG', 'Reg'],
      desconto_claro: ['Desconto Claro', 'DESCONTO_CLARO', 'desconto_claro', 'DESCONTO', 'Desconto'],
      grupo_hierarquia: ['Grupo Hierarquia', 'GRUPO_HIERARQUIA', 'grupo_hierarquia', 'HIERARQUIA', 'Hierarquia', 'GRUPO', 'Grupo'],
    }
  },
  PORTABILIDADE_VIVO: {
    label: 'Portabilidade Vivo',
    emoji: '🟣',
    cor: '#7c3aed',
    colunasObrigatorias: ['CNPJ', 'CLIENTE'],
    colunasOpcionais: [
      'CONSULTOR', 'M', 'VALOR ATUAL', 'LINHAS', 'DATA',
      'UF', 'CIDADE', 'ENDERECO', 'BAIRRO', 'NUMERO', 'CEP',
      'SITUACAO_RECEITA', 'CONTAS VIVO', 'FATLIQUIDO', 'ALTA', 'FIXO',
      'NOME_COMPLETO', 'CELULAR', 'OP CELULAR', 'TELEFONE', 'OP TELEFONE',
      'EMAIL', 'PRODUTO', 'NR_CNPJ', 'NM_CLIENTE', 'M CONSULTA', 
      'VALOR ATUAL CONSULTA', 'LINHAS MOVEIS CONSULTA', 'DATA CONSULTA',
      'DS_ENDERECO', 'DS_CIDADE', 'NR_CEP', 'NM_CONTATO', 
      'EMAIL_CONTATO_PRINCIPAL', 'CELULAR_CONTATO_PRINCIPAL',
      'TLFN_1', 'TLFN_2', 'TLFN_3', 'TLFN_4', 'TLFN_5',
      'VERTICAL', 'POSSE', 'PRIMEIRA_OFERTA', 'RECOMENDACAO',
      'QT_MOVEL_TERM', 'QT_BASICA_TERM_FIBRA', 'QT_BASICA_TERM_METALICO',
      'QT_BL_FTTH', 'QT_BL_FTTC',
      'QTD_LINHAS_FIXAS', 'VALOR_FATURA_FIXA'
    ],
    mapeamento: {
      // CNPJ - incluindo NR_CNPJ do MAPA Vivo
      cnpj: ['CNPJ', 'cnpj', 'Cnpj', 'CPF_CNPJ', 'cpf_cnpj', 'NR_CNPJ', 'Nr_Cnpj', 'nr_cnpj'],
      
      // Razão Social - incluindo NM_CLIENTE do MAPA Vivo e EMPRESA do layout Vivo×Claro
      razao_social: ['CLIENTE', 'cliente', 'RAZAO_SOCIAL', 'razao_social', 'Razao Social', 'RAZÃO SOCIAL', 'NOME', 'nome',
                     'NM_CLIENTE', 'Nm_Cliente', 'nm_cliente', 'EMPRESA', 'empresa', 'Empresa'],
      
      // Valor Fatura - incluindo VALOR ATUAL CONSULTA do MAPA Vivo
      valor_fatura_atual: ['VALOR ATUAL', 'VALOR_ATUAL', 'valor_atual', 'VALOR_FATURA_ATUAL', 'valor_fatura_atual', 
                          'Valor Atual', 'V.ATUAL', 'V. ATUAL', 'VALORATUAL', 'ValorAtual', 'Valor atual',
                          'VALOR ATUAL CONSULTA', 'VALOR_ATUAL_CONSULTA', 'valor_atual_consulta',
                          // MAPA Vivo — coluna "VALOR CONTA MOVEL" (com ou sem espaços)
                          'VALOR CONTA MOVEL', ' VALOR CONTA MOVEL ', 'VALOR_CONTA_MOVEL', 'valor conta movel', 'Valor Conta Movel'],
      
      // Quantidade de linhas - incluindo LINHAS MOVEIS CONSULTA do MAPA Vivo e LINHAS MOVEIS do layout Vivo×Claro
      qtd_linhas_voz: ['LINHAS', 'linhas', 'QTD_LINHAS_VOZ', 'qtd_linhas_voz', 'LINHAS_VOZ', 'Linhas Voz', 'QTD_LINHAS',
                       'LINHAS MOVEIS CONSULTA', 'LINHAS_MOVEIS_CONSULTA', 'linhas_moveis_consulta',
                       'LINHAS MOVEIS', 'LINHAS_MOVEIS', 'Linhas Moveis', 'LINHAS MÓVEIS', 'Linhas Móveis',
                       'QT_MOVEL_TERM', 'qt_movel_term'],
      
      // Tempo de contrato (M) - incluindo M CONSULTA e MES EM JAN26 do MAPA Vivo
      tempo_contrato_voz: ['M', 'm', 'MES', 'Mes', 'MESES', 'Meses', 'MES_REFERENCIA', 'TEMPO CONTRATO', 'tempo_contrato',
                           'M CONSULTA', 'M_CONSULTA', 'm_consulta', 'MCONSULTA',
                           // MAPA Vivo — coluna dinâmica "MES EM [MES][ANO]"
                           'MES EM JAN26', 'MES EM FEV26', 'MES EM MAR26', 'MES EM ABR26', 'MES EM MAI26',
                           'MES EM JUN26', 'MES EM JUL26', 'MES EM AGO26', 'MES EM SET26', 'MES EM OUT26',
                           'MES EM NOV26', 'MES EM DEZ26',
                           'MES EM JAN25', 'MES EM FEV25', 'MES EM MAR25', 'MES EM ABR25', 'MES EM MAI25',
                           'MES EM JUN25', 'MES EM JUL25', 'MES EM AGO25', 'MES EM SET25', 'MES EM OUT25',
                           'MES EM NOV25', 'MES EM DEZ25',
                           'MES EM JAN27', 'MES EM FEV27', 'MES EM MAR27', 'MES EM ABR27', 'MES EM MAI27',
                           'MES EM JUN27', 'MES EM JUL27', 'MES EM AGO27', 'MES EM SET27', 'MES EM OUT27',
                           'MES EM NOV27', 'MES EM DEZ27'],
      
      uf: ['UF', 'uf', 'ESTADO', 'estado', 'Estado'],
      
      // Cidade - incluindo DS_CIDADE do MAPA Vivo
      cidade: ['CIDADE', 'cidade', 'Cidade', 'MUNICIPIO', 'municipio', 'DS_CIDADE', 'Ds_Cidade', 'ds_cidade'],
      
      // Telefone - incluindo TLFN_1 do MAPA Vivo
      telefone: ['TELEFONE', 'telefone', 'Telefone', 'FONE', 'fone', 'TEL', 'tel', 
                 'TLFN_1', 'TLFN1', 'tlfn_1', 'tlfn1'],
      telefone_2: ['TLFN_2', 'TLFN2', 'tlfn_2', 'TELEFONE2', 'telefone_2'],
      telefone_3: ['TLFN_3', 'TLFN3', 'tlfn_3', 'TELEFONE3', 'telefone_3'],
      telefone_4: ['TLFN_4', 'TLFN4', 'tlfn_4', 'TELEFONE4', 'telefone_4'],
      telefone_5: ['TLFN_5', 'TLFN5', 'tlfn_5', 'TELEFONE5', 'telefone_5'],
      
      // Email - incluindo EMAIL_CONTATO_PRINCIPAL e EMAIL_CONTATO_PRINCIPAL_SFA do MAPA Vivo
      email: ['EMAIL', 'email', 'Email', 'E-MAIL', 'e-mail',
              'EMAIL_CONTATO_PRINCIPAL', 'email_contato_principal',
              'EMAIL_CONTATO_PRINCIPAL_SFA', 'email_contato_principal_sfa',
              'EMAIL GESTOR', 'EMAIL_GESTOR', 'Email Gestor'],
      
      conta_operadora: ['CONTAS VIVO', 'CONTAS_VIVO', 'contas_vivo', 'CONTA_OPERADORA', 'conta_operadora', 'CONTA', 'conta'],
      produto: ['PRODUTO', 'produto', 'Produto', 'PLANO', 'plano', 'TP_PRODUTO', 'tp_produto'],
      
      // Campos extras que irão para dados_discadora
      consultor_origem: ['CONSULTOR', 'consultor', 'Consultor'],
      mes_referencia_original: ['M', 'm', 'MES', 'Mes', 'MES_REFERENCIA', 'mes_referencia', 'M CONSULTA', 'M_CONSULTA'],
      
      // Data consulta - incluindo DATA CONSULTA do MAPA Vivo
      data_consulta: ['DATA', 'data', 'Data', 'DATA_CONSULTA', 'data_consulta', 'DATA CONSULTA'],
      
      // Endereço - incluindo DS_ENDERECO do MAPA Vivo
      endereco: ['ENDERECO', 'ENDEREÇO', 'endereco', 'Endereco', 'Endereço', 'DS_ENDERECO', 'Ds_Endereco', 'ds_endereco'],
      bairro: ['BAIRRO', 'bairro', 'Bairro'],
      
      // Numero - incluindo NUMERO do MAPA Vivo
      numero_endereco: ['NUMERO', 'numero', 'Numero', 'NÚMERO', 'NUM', 'num'],
      
      // CEP - incluindo NR_CEP do MAPA Vivo
      cep: ['CEP', 'cep', 'Cep', 'NR_CEP', 'Nr_Cep', 'nr_cep'],
      
      situacao_receita: ['SITUACAO_RECEITA', 'SITUAÇÃO_RECEITA', 'situacao_receita', 'Situacao Receita'],
      fatliquido: ['FATLIQUIDO', 'FAT_LIQUIDO', 'fatliquido', 'FatLiquido', 'Fat Liquido'],
      alta: ['ALTA', 'alta', 'Alta'],
      fixo: ['FIXO', 'fixo', 'Fixo'],
      
      // Nome contato - incluindo NM_CONTATO e NM_CONTATO_SFA do MAPA Vivo e NOME GESTOR do layout Vivo×Claro
      nome_completo: ['NOME_COMPLETO', 'nome_completo', 'Nome Completo', 'NOME_CONTATO', 'nome_contato',
                      'NM_CONTATO', 'Nm_Contato', 'nm_contato', 'NM_CONTATO_SFA', 'nm_contato_sfa',
                      'CONTATO', 'contato', 'NOME GESTOR', 'NOME_GESTOR', 'Nome Gestor'],
      
      // Celular - incluindo CELULAR_CONTATO_PRINCIPAL e CELULAR_CONTATO_PRINCIPAL_SFA do MAPA Vivo e TELEFONE GESTOR do layout Vivo×Claro
      celular: ['CELULAR', 'celular', 'Celular', 'CELULAR_CONTATO_PRINCIPAL', 'celular_contato_principal',
                'CELULAR_CONTATO_PRINCIPAL_SFA', 'celular_contato_principal_sfa',
                'TELEFONE GESTOR', 'TELEFONE_GESTOR', 'Telefone Gestor'],
      
      operadora_celular: ['OP CELULAR', 'OP_CELULAR', 'op_celular', 'OP CELUALR', 'Operadora Celular', 'OPERADORA', 'operadora'],
      operadora_telefone: ['OP TELEFONE', 'OP_TELEFONE', 'op_telefone', 'Operadora Telefone'],
      
      // Campos extras do MAPA Vivo (vão para dados_discadora)
      vertical: ['VERTICAL', 'vertical', 'Vertical'],
      posse: ['POSSE', 'posse', 'Posse'],
      primeira_oferta: ['PRIMEIRA_OFERTA', 'primeira_oferta', 'Primeira Oferta', 'PRIMEIRA OFERTA'],
      recomendacao: ['RECOMENDACAO', 'recomendacao', 'Recomendacao', 'RECOMENDAÇÃO', 'Recomendação'],
      
      // Campos técnicos do MAPA Vivo
      qt_movel_term: ['QT_MOVEL_TERM', 'qt_movel_term'],
      qt_movel_pen: ['QT_MOVEL_PEN', 'qt_movel_pen'],
      qt_basica_term_fibra: ['QT_BASICA_TERM_FIBRA', 'qt_basica_term_fibra'],
      qt_basica_term_metalico: ['QT_BASICA_TERM_METALICO', 'qt_basica_term_metalico'],
      // Banda Larga — layout Vivo×Claro traz coluna única "BANDA LARGA" (nº de acessos); tratada como fibra (FTTH)
      qt_bl_ftth: ['QT_BL_FTTH', 'qt_bl_ftth', 'BANDA LARGA', 'BANDA_LARGA', 'Banda Larga'],
      qt_bl_fttc: ['QT_BL_FTTC', 'qt_bl_fttc'],
      
      // CNAE
      cnae: ['DS_CNAE', 'ds_cnae', 'CNAE', 'cnae'],
      atividade_economica: ['DS_ATIVIDADE_ECONOMICA', 'ds_atividade_economica', 'ATIVIDADE_ECONOMICA'],

      // Fixa — quantidade de linhas e valor da fatura
      qtd_linhas_fixas: ['QTD_LINHAS_FIXAS', 'qtd_linhas_fixas', 'LINHAS FIXAS', 'Linhas Fixas', 'QTD FIXA', 'Qtd Fixa', 'QTD_FIXA', 'linhas_fixas', 'LINHA FIXA', 'LINHA_FIXA', 'Linha Fixa'],
      valor_fatura_fixa: ['VALOR_FATURA_FIXA', 'valor_fatura_fixa', 'VALOR FATURA FIXA', 'Valor Fatura Fixa', 'VALOR FIXA', 'Valor Fixa', 'VALOR CONTA FIXA', 'VL FIXA', 'vl_fixa'],
    }
  },
  TT_CPF_CNPJ: {
    label: 'TT CPF→CNPJ',
    emoji: '🔵',
    cor: '#2563eb',
    colunasObrigatorias: ['CNPJ', 'NOME'],
    colunasOpcionais: [
      'CPF/CNPJ do cliente', 'RazaoSocial', 'NomeFantasia',
      'TELEFONE 1', 'TELEFONE 2', 'TELEFONE 3', 'TELEFONE 4',
      'CIDADE', 'CEP', 'BAIRRO', 'NUMERO', 'UF', 'ENDEREÇO', 'EMAIL',
      'rua', 'estado', 'complemento',
      'Status', 'MOVEL', 'COMBO', 'RESIDENCIAL',
      'Data Fatura', 'DATA/ALT', 'PLANO', 'SMS',
      'Movel 1', 'Movel 2', 'Movel 3', 'Movel 4', 'Movel 5', 'Movel 6',
      'MUDANÇA DE ENDEREÇO ABERTA/FEITA', 'NOVO ENDEREÇO'
    ],
    mapeamento: {
      // Dados do Cliente PF
      doc_cpf: ['CPF/CNPJ do cliente', 'CPF_CNPJ', 'CPF', 'cpf', 'DOC_CPF', 'doc_cpf', 'CPF_PROPRIETARIO', 'CPF/CNPJ'],
      nome_proprietario: ['NOME', 'Nome', 'NOME_PROPRIETARIO', 'nome_proprietario', 'Nome Proprietário', 'PROPRIETARIO'],
      
      // Dados da Empresa
      cnpj: ['CNPJ', 'cnpj', 'Cnpj', 'CNPJ_EMPRESA', 'PJ', 'pj'],
      razao_social: ['RazaoSocial', 'RAZAO_SOCIAL', 'Razao Social', 'razao_social', 'RAZÃO SOCIAL', 'EMPRESA', 'NOME DA EMPRESA', 'Nome Da Empresa', 'nome da empresa', 'NOME_DA_EMPRESA', 'nome_empresa', 'NOME_EMPRESA'],
      nome_fantasia: ['NomeFantasia', 'NOME_FANTASIA', 'Nome Fantasia', 'FANTASIA', 'nome_fantasia'],
      endereco_empresa: ['ENDEREÇO', 'endereco', 'ENDERECO', 'Endereço', 'ENDERECO_EMPRESA', 'endereco_empresa', 'rua', 'RUA', 'Rua', 'LOGRADOURO', 'logradouro', 'Logradouro'],
      
      // Telefones
      telefone: ['TELEFONE 1', 'TELEFONE1', 'telefone1', 'TELEFONE', 'telefone', 'Telefone'],
      telefone_2: ['TELEFONE 2', 'TELEFONE2', 'telefone2'],
      telefone_3: ['TELEFONE 3', 'TELEFONE3', 'telefone3'],
      telefone_4: ['TELEFONE 4', 'TELEFONE4', 'telefone4'],
      
      // Localização
      uf: ['UF', 'uf', 'ESTADO', 'estado', 'Estado'],
      cidade: ['CIDADE', 'cidade', 'Cidade', 'MUNICIPIO', 'municipio'],
      cep: ['CEP', 'cep', 'Cep'],
      bairro: ['BAIRRO', 'bairro', 'Bairro'],
      numero_endereco: ['NUMERO', 'numero', 'Numero', 'NÚMERO', 'NUM', 'num'],
      email: ['EMAIL', 'email', 'Email', 'E-MAIL', 'e-mail'],
      
      // Valores e Planos (irão para dados_discadora)
      qtd_linhas_voz: ['QTD_MOVEL', 'qtd_movel', 'QTD MOVEL', 'QTD_LINHAS_VOZ', 'qtd_linhas_voz', 'LINHAS'],
      valor_movel: ['MOVEL', 'movel', 'Movel', 'MÓVEL', 'móvel', 'VALOR_MOVEL', 'valor_movel', 'Móvel Valor', 'Mobile', 'MOBILE', 'mobile'],
      valor_combo: ['COMBO', 'combo', 'Combo', 'VALOR_COMBO'],
      valor_residencial: ['RESIDENCIAL', 'residencial', 'VALOR_RESIDENCIAL', 'Residencial', 'residential', 'RESIDENTIAL', 'Residential'],
      data_fatura: ['Data Fatura', 'DATA_FATURA', 'data_fatura', 'DataFatura', 'DATA/ALT', 'DATA ALT', 'DATALT', 'Data Alt'],
      produto: ['PLANO', 'Plano', 'plano', 'PRODUTO', 'Produto'],
      sms_status: ['SMS', 'sms', 'Sms'],
      // Movel 1..18: as planilhas de maio/junho chegam a 18 linhas por conta
      ...Object.fromEntries(
        Array.from({ length: MAX_LINHAS_MOVEL }, (_, i) => [
          `movel_${i + 1}`,
          [`Movel ${i + 1}`, `MOVEL_${i + 1}`, `movel_${i + 1}`, `MOVEL${i + 1}`, `Móvel ${i + 1}`],
        ])
      ),
      complemento: ['complemento', 'COMPLEMENTO', 'Complemento', 'COMPL'],
      novo_endereco: ['NOVO ENDEREÇO', 'NOVO_ENDERECO', 'novo_endereco', 'Novo Endereço', 'NovoEndereco', 'MUD END', 'MUD. END', 'MUD END.', 'MUD ENDEREÇO', 'MUDANÇA DE ENDEREÇO', 'MUDANÇA ENDEREÇO', 'MUDANCA ENDERECO', 'Endereço SQL', 'ENDERECO SQL'],
      status_planilha: ['Status', 'STATUS', 'status'],
      mudanca_endereco: ['MUDANÇA DE ENDEREÇO ABERTA/FEITA', 'MUDANCA_ENDERECO', 'mudanca_endereco', 'MUDANCA DE ENDERECO', 'Mudança de Endereço'],

      // Dados PJ completos (salvam em dados_discadora)
      socios: ['SOCIOS', 'Socios', 'socios', 'SÓCIOS', 'Sócios', 'NOME DOS SOCIOS', 'NOMES DOS SOCIOS', 'NOME DOS SÓCIOS', 'QUADRO SOCIETARIO', 'QUADRO SOCIETÁRIO'],
      situacao_cadastral_pj: ['SITUACAO_CADASTRAL', 'Situacao Cadastral', 'SITUAÇÃO CADASTRAL', 'situacao_cadastral'],
      cnae_principal: ['CNAE PRINCIPAL', 'CNAE_PRINCIPAL', 'cnae_principal', 'CNAE Principal'],
      simples_nacional: ['SIMPLES', 'Simples', 'simples', 'SIMPLES NACIONAL', 'Simples Nacional'],
      natureza_juridica: ['NATUREZA JURIDICA', 'NATUREZA_JURIDICA', 'natureza_juridica', 'Natureza Juridica', 'NATUREZA JURÍDICA'],
      mei: ['MEI', 'Mei', 'mei'],
      data_situacao_cadastral: ['DATA SITUACAO CADASTRAL', 'DATA_SITUACAO_CADASTRAL', 'Data Situacao Cadastral', 'DATA SITUAÇÃO CADASTRAL'],
      porte_empresa: ['PORTE', 'Porte', 'porte', 'PORTE EMPRESA'],
      cidade_pj: ['CITY', 'City', 'city', 'CIDADE PJ', 'CIDADE_PJ', 'CIDADE_1', 'CIDADE 1', 'CIDADE.1'],
      estado_pj: ['ESTADO', 'Estado'],
      telefone_pj_1: ['TELEFONE_PJ_1', 'TELEFONE PJ 1', 'Tel PJ 1'],
      telefone_pj_2: ['TELEFONE_PJ_2', 'TELEFONE PJ 2', 'Tel PJ 2'],
      
      // Campos legacy para compatibilidade
      linhas_cpf: ['LINHAS_CPF', 'linhas_cpf', 'QTD_LINHAS_CPF', 'Linhas CPF'],
      tipo_linha: ['TIPO_LINHA', 'tipo_linha', 'TIPO', 'tipo', 'Tipo Linha'],
      ano_base: ['ANO_BASE', 'ano_base', 'ANO', 'ano', 'Ano Base'],
    }
  },
  // ─── ALGAR ────────────────────────────────────────────────────────────────
  // Base da Algar (31/08/2026). A planilha vem com 30 colunas e METADE VAZIA:
  // DATA ABERTURA, OPERADORA, CONTATO FIXO, OPERADORA FIXO, CREDITO,
  // VELOCIDADE, PRODUTO BASE, QTD LINHA MOVEL, M MOVEL, VALOR FAT MOVEL,
  // PLANO INTERNET, M FIXA, VALOR FAT FIXA e VALOR PLANO INTERNET nao vieram
  // preenchidas nesta remessa.
  //
  // ⚠️ MAPEAMOS TODAS AS 30 ASSIM MESMO, de proposito. O cabecalho da Algar ja'
  // preve' esses campos; quando a proxima lista chegar com eles preenchidos, ela
  // entra completa SEM tocar em codigo. Coluna vazia simplesmente nao vira dado.
  ALGAR: {
    label: 'Algar',
    emoji: '🟣',
    cor: '#7A2235',
    colunasObrigatorias: ['CNPJ'],
    colunasOpcionais: [
      'NOME', 'RAZÃO SOCIAL', 'SOCIO ADM', 'TELEFONE', 'NUMERO TEL', 'EMAIL',
      'MEI', 'CNAE', 'ENDEREÇO COMPLETO', 'NUMERO', 'CEP', 'CIDADE', 'UF',
      'COBERTURA', 'DATA ABERTURA', 'OPERADORA', 'CONTATO FIXO',
      'OPERADORA FIXO', 'CREDITO', 'VELOCIDADE', 'PRODUTO BASE',
      'QTD LINHA MOVEL', 'M MOVEL', 'VALOR FAT MOVEL', 'PLANO INTERNET',
      'M FIXA', 'VALOR FAT FIXA', 'VALOR PLANO INTERNET',
    ],
    mapeamento: {
      cnpj: ['CNPJ', 'cnpj', 'Cnpj'],
      razao_social: ['RAZÃO SOCIAL', 'RAZAO SOCIAL', 'razao_social', 'NOME', 'Nome'],
      nome_proprietario: ['NOME', 'Nome'],
      socios: ['SOCIO ADM', 'SOCIOS', 'SÓCIO ADM', 'Socio Adm', 'SOCIO_ADM'],
      // NUMERO TEL e' copia identica de TELEFONE nas 666 linhas da 1a remessa —
      // fica como alias, nao como segundo telefone, pra nao duplicar contato.
      telefone: ['TELEFONE', 'NUMERO TEL', 'telefone', 'Telefone'],
      telefone_2: ['CONTATO FIXO', 'TELEFONE 2', 'TELEFONE2'],
      email: ['EMAIL', 'email', 'E-MAIL'],
      mei: ['MEI', 'Mei', 'mei'],
      cnae_principal: ['CNAE', 'CNAE PRINCIPAL', 'cnae'],
      endereco_empresa: ['ENDEREÇO COMPLETO', 'ENDERECO COMPLETO', 'ENDEREÇO', 'ENDERECO'],
      numero_endereco: ['NUMERO', 'NÚMERO', 'numero'],
      cep: ['CEP', 'cep'],
      cidade: ['CIDADE', 'cidade', 'Cidade'],
      uf: ['UF', 'uf', 'ESTADO'],
      // COBERTURA vem 'COM COBERTURA' / 'SEM COBERTURA' — e' a UNICA regua de
      // priorizacao que a remessa de 31/08 entrega (381 com, 285 sem).
      cobertura_fibra: ['COBERTURA', 'cobertura', 'COBERTURA FIBRA'],
      // Os que vieram vazios em 31/08 e devem entrar sozinhos quando chegarem:
      operadora_atual: ['OPERADORA', 'OPERADORA ATUAL', 'operadora'],
      qtd_linhas_voz: ['QTD LINHA MOVEL', 'QTD_LINHA_MOVEL', 'QTD LINHAS MOVEL'],
      tempo_contrato_voz: ['M MOVEL', 'M_MOVEL', 'M MÓVEL'],
      valor_fatura_atual: ['VALOR FAT MOVEL', 'VALOR_FAT_MOVEL', 'VALOR FATURA MOVEL'],
      valor_fatura_fixa: ['VALOR FAT FIXA', 'VALOR_FAT_FIXA'],
      tempo_contrato_bl: ['M FIXA', 'M_FIXA'],
      plano_atual: ['PLANO INTERNET', 'PLANO_INTERNET', 'PRODUTO BASE'],
      produto: ['PRODUTO BASE', 'PRODUTO_BASE'],
    },
  },

  // 🔵 OI: lista de cliente de fibra da Oi BLOQUEADO ou SUSPENSO, com o valor
  // que ele paga hoje e um carimbo de cobertura. E' a primeira lista do CRM que
  // traz PESSOA e EMPRESA na mesma planilha (coluna tipo_pessoa) — por isso e'
  // ela que estreia o lead PF.
  //
  // ⚠️ O mapeamento fica VAZIO de proposito. O layout da Oi tem quatro pares de
  // colunas que a busca fuzzy confunde entre si — 'CPF' x 'CNPJ',
  // 'TELEFONE 1' x 'TELEFONE1', 'CIDADE' (instalacao) e 'NUMERO' — e a coluna
  // 'CPF' chega a guardar um CNPJ quando tipo_pessoa e' PJ. Cada uma dessas
  // trocas poe o documento errado no campo errado, calado. Como o layout e'
  // contrato fixo do fornecedor, OI e' lida por NOME EXATO em mapearOi().
  OI: {
    label: 'Oi',
    emoji: '🔵',
    cor: '#7A2235',
    colunasObrigatorias: ['CPF', 'tipo_pessoa', 'nome_cliente'],
    colunasOpcionais: [
      'acesso_gpon', 'grupo_unidade', 'id_conta', 'codigo_estacao', 'ds_servico',
      'ds_tipo_servico', 'grupo_bloqueio', 'statusbloq', 'data_do_pedido',
      'classe_produto', 'linha_produto', 'tipo_produto', 'tipo_logradouro_inst',
      'nome_logradouro_inst', 'NUMERO', 'tipo_complemento_instalacao',
      'numero_complemento_1_inst', 'numero_complemento_2_inst',
      'numero_complemento_3_inst', 'bairro_instalacao', 'CIDADE', 'UF', 'CEP',
      'ACESSO_GPON', 'TELEFONE 1', 'TELEFONE 2', 'TELEFONE 3', 'VALOR FATURA',
      'CNPJ', 'RAZAO SOCIAL', 'NOME DOS SOCIOS', 'TELEFONE1', 'TELEFONE2',
      'EMAIL_RFB', 'SITUACAO_CADASTRAL', 'CNAE PRINCIPAL', 'SIMPLES',
      'NATUREZA JURIDICA', 'MEI', 'PORTE', 'COBERTURA',
    ],
    mapeamento: {},
  }
};

/**
 * O fornecedor gera a lista com encoding misturado: 2.272 celulas da remessa de
 * 09/09/2026 chegaram como "SuspensÃ£o" em vez de "Suspensão" (UTF-8 lido como
 * latin-1). Desfazemos relendo os bytes — so' quando a marca esta la', e nunca
 * em texto que ja tem caractere fora da faixa de 1 byte.
 */
export function consertarMojibake(valor: unknown): string {
  const s = valor === null || valor === undefined ? '' : String(valor);
  if (!/[ÃÂ]/.test(s)) return s;
  const codes = [...s].map(c => c.charCodeAt(0));
  if (codes.some(c => c > 255)) return s;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(codes));
  } catch {
    return s;
  }
}

/**
 * Detecta o tipo de lead baseado nas colunas da planilha
 */
export function detectarTipoLead(colunas: string[]): string | null {
  const colunasNormalizadas = colunas.map(c => c.toUpperCase().trim().replace(/\s+/g, '_'));
  const colunasStr = colunasNormalizadas.join(' ');

  // ─── DETECÇÃO PRIORITÁRIA POR PALAVRAS-CHAVE EXCLUSIVAS ───────────────────
  // Executada ANTES do loop genérico para evitar falsos positivos entre tipos.

  // OI: identificada pelo acesso GPON e pelo bloco de bloqueio da Oi. Nenhuma
  // outra lista do CRM tem essas colunas, e ela entra primeiro porque traz
  // TIPO_PESSOA e CIDADE, que outros tipos também usam.
  if (
    colunasStr.includes('ACESSO_GPON') ||
    colunasStr.includes('GRUPO_BLOQUEIO') ||
    colunasStr.includes('STATUSBLOQ') ||
    (colunasStr.includes('DS_TIPO_SERVICO') && colunasStr.includes('TIPO_PESSOA'))
  ) {
    return 'OI';
  }


  // PORTABILIDADE_VIVO: identificada pela coluna "CLIENTE" (exclusiva desta planilha)
  // ou por marcadores inequívocos do MAPA Vivo / planilha Vivo.
  // ⚠️ "CLIENTE" como token isolado — não confundir com "NM_CLIENTE" (Claro)
  if (
    colunasNormalizadas.includes('CLIENTE') ||
    colunasStr.includes('OP_CELULAR') ||
    colunasStr.includes('CONTAS_VIVO') ||
    colunasStr.includes('OPERADORA_ATUAL') ||
    colunasStr.includes('CONTA_OPERADORA') ||
    colunasStr.includes('PORTABILIDADE') ||
    colunasStr.includes('QT_MOVEL_TERM') ||
    colunasStr.includes('QT_BASICA_TERM_FIBRA') ||
    colunasStr.includes('QT_BASICA_TERM_METALICO') ||
    colunasStr.includes('QT_BL_FTTH') ||
    colunasStr.includes('QT_BL_FTTC') ||
    colunasStr.includes('VALOR_FATURA_FIXA') ||
    colunasStr.includes('QTD_LINHAS_FIXAS')
  ) {
    return 'PORTABILIDADE_VIVO';
  }

  // RENOVACAO_CLARO: tempo de contrato é marcador exclusivo desta planilha
  if (
    colunasStr.includes('TEMPO_CONTRATO') ||
    colunasStr.includes('TEMPO_DE_CONTRATO') ||
    colunasStr.includes('DATA_LIMITE_RENOVACAO') ||
    colunasStr.includes('TEMPO_DE_CONTRATO_(VOZ)') ||
    colunasStr.includes('TEMPO_DE_CONTRATO_(BANDA_LARGA)')
  ) {
    return 'RENOVACAO_CLARO';
  }

  // ALGAR: a planilha tem colunas que so' existem nela. Checado ANTES da TT
  // porque as duas tem NOME + CNPJ + TELEFONE — sem esta ordem a base da Algar
  // seria lida como TT e cairia na campanha errada.
  if (
    colunasStr.includes('PRODUTO_BASE')
    || colunasStr.includes('VALOR_FAT_MOVEL')
    || colunasStr.includes('QTD_LINHA_MOVEL')
    || colunasStr.includes('PLANO_INTERNET')
    || colunasStr.includes('SOCIO_ADM')
    || (colunasNormalizadas.includes('COBERTURA')
        && colunasNormalizadas.includes('CNAE')
        && colunasNormalizadas.includes('MEI'))
  ) {
    return 'ALGAR';
  }

  // TT CPF→CNPJ: marcadores exclusivos desta planilha
  // ⚠️ Checar 'NOME' como token isolado (não 'NOME_COMPLETO') e 'MOVEL' isolado
  //    para evitar falsos positivos com QT_MOVEL_TERM e NOME_COMPLETO da Vivo.
  if (
    colunasStr.includes('DOC_CPF') ||
    colunasStr.includes('NOME_PROPRIETARIO') ||
    colunasStr.includes('CPF_PROPRIETARIO') ||
    colunasStr.includes('RAZAOSOCIAL') ||
    colunasStr.includes('NOMEFANTASIA') ||
    colunasNormalizadas.includes('MOVEL_1') ||
    colunasNormalizadas.includes('MOVEL_2') ||
    colunasNormalizadas.includes('MOVEL_3') ||
    colunasStr.includes('TELEFONE_1') ||
    colunasStr.includes('TELEFONE_2') ||
    colunasStr.includes('TELEFONE_3') ||
    colunasStr.includes('TELEFONE_4') ||
    colunasStr.includes('CPF/CNPJ_DO_CLIENTE') ||
    colunasStr.includes('CPFCNPJDOCLIENTE') ||
    // "NOME" isolado (sem sufixo) + "MOVEL" isolado (sem prefixo QT_) + CNPJ
    (colunasNormalizadas.includes('NOME') &&
     colunasNormalizadas.includes('CNPJ') &&
     (colunasNormalizadas.includes('MOVEL') || colunasStr.includes('COMBO') || colunasStr.includes('RESIDENCIAL')))
  ) {
    return 'TT_CPF_CNPJ';
  }

  // ─── LOOP GENÉRICO (fallback) ──────────────────────────────────────────────
  // Para tipos que não foram capturados pelos atalhos acima.
  for (const [tipo, config] of Object.entries(TIPOS_LEAD_CONFIG)) {
    const colunasConfig = config.colunasObrigatorias.map(c => c.toUpperCase());

    let encontradas = 0;
    for (const colObrig of colunasConfig) {
      const mapeamento = config.mapeamento;
      const camposDB = Object.entries(mapeamento);

      for (const [campo, aliases] of camposDB) {
        const aliasesUpper = aliases.map(a => a.toUpperCase().replace(/\s+/g, '_'));
        if (aliasesUpper.some(alias => colunasNormalizadas.includes(alias))) {
          if (colObrig === campo.toUpperCase() || aliasesUpper.includes(colObrig)) {
            encontradas++;
            break;
          }
        }
      }
    }

    if (encontradas >= colunasConfig.length * 0.7) {
      return tipo;
    }
  }

  return null;
}

/**
 * 🔥 NORMALIZAÇÃO ULTRA-AGRESSIVA: Remove TUDO exceto letras e números
 */
function normalizarUltraAgressivo(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9]/g, '');       // Remove TUDO exceto letras e números
}

/**
 * Normaliza string para busca fuzzy: lowercase, remove espaços, parênteses, underscores, acentos
 */
function normalizarParaFuzzy(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[_\s\(\)\-\.]/g, '');  // Remove _, espaços, parênteses, hífens, pontos
}

/**
 * Busca fuzzy de coluna - compara normalizado
 */
function buscarColunaFuzzy(colunaPlanilha: string, aliases: string[]): boolean {
  const colunaFuzzy = normalizarParaFuzzy(colunaPlanilha);
  return aliases.some(alias => normalizarParaFuzzy(alias) === colunaFuzzy);
}

/**
 * 🔥 BUSCA ULTRA-AGRESSIVA: Remove absolutamente tudo exceto letras
 */
function buscarColunaUltraAgressivo(colunaPlanilha: string, aliases: string[]): boolean {
  const colunaUltra = normalizarUltraAgressivo(colunaPlanilha);
  return aliases.some(alias => normalizarUltraAgressivo(alias) === colunaUltra);
}

/**
 * Telefone da planilha vem com espaço no meio (" 18  99731 9737", "  4  13383 3627").
 * Deixa só dígitos, tira o 55 de país e descarta o que não é telefone com DDD.
 */
export function limparTelefonePlanilha(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  let digitos = String(valor).replace(/\D/g, '');
  if (digitos.length === 12 && digitos.startsWith('55')) digitos = digitos.slice(2);
  if (digitos.length === 13 && digitos.startsWith('55')) digitos = digitos.slice(2);
  if (digitos.length !== 10 && digitos.length !== 11) return null;
  return digitos;
}

/**
 * Valor da conta: aceita "R$ 110.42" (ponto decimal) e "R$ 2.118,64" (padrão BR).
 */
export function parseValorPlanilha(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'number') return isNaN(valor) ? null : valor;
  const str = String(valor).replace(/R\$/gi, '').replace(/\s/g, '').trim();
  if (!str) return null;
  const temVirgula = str.includes(',');
  const temPonto = str.includes('.');
  let limpo = str;
  if (temVirgula && temPonto) {
    limpo = str.replace(/\./g, '').replace(',', '.');
  } else if (temVirgula) {
    limpo = str.replace(',', '.');
  }
  const num = parseFloat(limpo);
  return isNaN(num) ? null : num;
}

/**
 * Data da planilha: serial do Excel (39653), ISO ("2018-12-06 00:00:00") ou BR → DD/MM/YYYY.
 */
export function formatarDataPlanilha(valor: unknown): string | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const str = String(valor).trim();
  if (!str) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  if (/^\d+$/.test(str)) {
    const serial = parseInt(str, 10);
    if (serial > 20000 && serial < 60000) {
      const d = new Date(Math.round((serial - 25569) * 86400 * 1000));
      const dia = String(d.getUTCDate()).padStart(2, '0');
      const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
      return `${dia}/${mes}/${d.getUTCFullYear()}`;
    }
  }
  return str;
}

/**
 * Dígito verificador do CNPJ. É ele que separa "o Excel comeu o zero da frente"
 * de "esse número está truncado no meio": repor zeros só é seguro quando o
 * resultado fecha o DV.
 */
export function cnpjTemDVValido(digitos: string): boolean {
  if (digitos.length !== 14 || /^(\d)\1{13}$/.test(digitos)) return false;
  const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (const pos of [12, 13]) {
    const peso = pesos.slice(13 - pos);
    let soma = 0;
    for (let i = 0; i < pos; i++) soma += Number(digitos[i]) * peso[i];
    const resto = soma % 11;
    if (Number(digitos[pos]) !== (resto < 2 ? 0 : 11 - resto)) return false;
  }
  return true;
}

/** Dígito verificador do CPF — mesma função de guarda do CNPJ. */
export function cpfTemDVValido(digitos: string): boolean {
  if (digitos.length !== 11 || /^(\d)\1{10}$/.test(digitos)) return false;
  for (const pos of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < pos; i++) soma += Number(digitos[i]) * (pos + 1 - i);
    const dv = (soma * 10) % 11;
    if (Number(digitos[pos]) !== (dv === 10 ? 0 : dv)) return false;
  }
  return true;
}

/**
 * CNPJ da planilha: só dígitos, trunca acima de 14 e recompõe o zero à esquerda
 * que o Excel come (13 dígitos → 14). Sem CNPJ, o lead não entra.
 *
 * ⚠️ Repor zero só vale se o DV fechar. A planilha grava o CNPJ como NÚMERO, e
 * todo CNPJ que começa com zero volta curto — na base TT de 28/08/2026 eram
 * 1.654 linhas de 11/12/13 dígitos, e as 1.654 fecharam o DV depois do padStart.
 * Já um número truncado no MEIO também tem 13 dígitos e NÃO fecha: sem essa
 * checagem o padStart o transformaria num CNPJ plausível que não existe — o
 * mesmo estrago dos 1.881 leads falsos de 31/07/2026, por outra porta.
 */
export function normalizarCNPJPlanilha(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  const str = String(valor).trim();
  if (!str) return null;
  // "3.21913E+13" já perdeu dígito no Excel — reconstruir daria um CNPJ inventado
  if (/\d[eE][+-]?\d/.test(str)) return null;
  let digitos = str.replace(/\D/g, '');
  if (!digitos) return null;
  // ⛔ 03/09/2026: era substring(0,14), que corta o FIM e inventa CNPJ —
  // 001105459000112 virava 00110545900011. Zero a mais se tira pela ESQUERDA.
  while (digitos.length > 14 && digitos.startsWith('0')) digitos = digitos.slice(1);
  if (digitos.length > 14) return null;
  if (digitos.length === 14) return digitos;
  if (digitos.length < 11) return null;
  const comZeros = digitos.padStart(14, '0');
  return cnpjTemDVValido(comZeros) ? comZeros : null;
}

/**
 * CPF da planilha: mesma história do CNPJ. Gravado como número, todo CPF que
 * começa com zero chega curto — na base TT de 28/08/2026 eram 6.566 linhas de
 * 7 a 10 dígitos, metade do arquivo, e todas fecharam o DV com os zeros de
 * volta. Sem isso o lead nasce com um CPF que não é de ninguém.
 */
export function normalizarCPFPlanilha(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  const digitos = String(valor).replace(/\D/g, '');
  if (!digitos) return null;
  if (digitos.length < 11) {
    const comZeros = digitos.padStart(11, '0');
    if (cpfTemDVValido(comZeros)) return comZeros;
  }
  return digitos;
}

const FAIXAS_CEP_UF: Record<string, Array<[number, number]>> = {
  SP: [[1000, 19999]], RJ: [[20000, 28999]], ES: [[29000, 29999]], MG: [[30000, 39999]],
  BA: [[40000, 48999]], SE: [[49000, 49999]], PE: [[50000, 56999]], AL: [[57000, 57999]],
  PB: [[58000, 58999]], RN: [[59000, 59999]], CE: [[60000, 63999]], PI: [[64000, 64999]],
  MA: [[65000, 65999]], PA: [[66000, 68899]], AP: [[68900, 68999]], AM: [[69000, 69299], [69400, 69899]],
  RR: [[69300, 69399]], AC: [[69900, 69999]], DF: [[70000, 72799], [73000, 73699]],
  GO: [[72800, 72999], [73700, 76799]], RO: [[76800, 76999]], TO: [[77000, 77999]],
  MT: [[78000, 78899]], MS: [[79000, 79999]], PR: [[80000, 87999]], SC: [[88000, 89999]],
  RS: [[90000, 99999]],
};

function cepEhDaUf(cep: string, uf: string): boolean {
  const prefixo = Number(cep.slice(0, 5));
  return (FAIXAS_CEP_UF[uf] || []).some(([de, ate]) => prefixo >= de && prefixo <= ate);
}

/**
 * CEP da planilha: 8 dígitos sempre. Gravado como número, o CEP de São Paulo
 * (01310-100) chega com 7 e vira um CEP de outro estado na hora de casar
 * endereço ou consultar cobertura.
 *
 * Com 7 dígitos há dois estragos possíveis, e a UF da linha decide qual foi
 * (Douglas 28/10/2026): o zero da frente que o Excel come (SP) ou o último
 * dígito cortado (base Vivo PF RS: 9687800 é 96878-000, Vale do Sol). Sem a UF,
 * ou quando ela não desempata, vale o zero da frente, como sempre foi. O dígito
 * cortado não volta: entra 0, o CEP geral do trecho, que fica na cidade certa.
 */
export function normalizarCEPPlanilha(valor: unknown, uf?: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  const digitos = String(valor).replace(/\D/g, '');
  if (!digitos || digitos.length > 8) return digitos || null;
  if (digitos.length === 7) {
    const sigla = String(uf ?? '').trim().toUpperCase();
    if (FAIXAS_CEP_UF[sigla] && !cepEhDaUf('0' + digitos, sigla) && cepEhDaUf(digitos + '0', sigla)) {
      return digitos + '0';
    }
  }
  return digitos.padStart(8, '0');
}

/**
 * 🆕 Mapeamento direto de colunas críticas - força correspondência
 * ✅ ATUALIZADO: Incluindo TODAS as variações possíveis de nomes de coluna
 */
const MAPEAMENTO_FORCADO: Record<string, string> = {
  // tempo_contrato_voz - TODAS as variações conhecidas após normalização ultra
  'tempodecontratovoz': 'tempo_contrato_voz',
  'tempocontratovoz': 'tempo_contrato_voz',
  'tempovoz': 'tempo_contrato_voz',
  'mesesvoz': 'tempo_contrato_voz',
  'mesescontratovoz': 'tempo_contrato_voz',
  // tempo_contrato_bl
  'tempodecontratobandalarga': 'tempo_contrato_bl',
  'tempocontratobl': 'tempo_contrato_bl',
  'tempobl': 'tempo_contrato_bl',
  'tempodados': 'tempo_contrato_bl',
  'tempodecontratodados': 'tempo_contrato_bl',
  'mesescontratobandalarga': 'tempo_contrato_bl',
  'mesescontratobl': 'tempo_contrato_bl',
  // tempo_contrato_telemetria - NOVOS
  'tempodecontratotelemetria': 'tempo_contrato_telemetria',
  'tempocontratotelemetria': 'tempo_contrato_telemetria',
  'tempotelemetria': 'tempo_contrato_telemetria',
  'mesestelemetria': 'tempo_contrato_telemetria',
  'mesescontratotelemetria': 'tempo_contrato_telemetria',
  // qtd_linhas_voz
  'quantidadedelinhasvoz': 'qtd_linhas_voz',
  'qtdlinhasvoz': 'qtd_linhas_voz',
  'linhasvoz': 'qtd_linhas_voz',
  'qtdelinhasvoz': 'qtd_linhas_voz',
  // qtd_linhas_bl
  'quantidadedelinhasbandalarga': 'qtd_linhas_bl',
  'qtdlinhasbl': 'qtd_linhas_bl',
  'linhasbl': 'qtd_linhas_bl',
  'qtdelinhasbl': 'qtd_linhas_bl',
  'qtdelinhasbandalarga': 'qtd_linhas_bl',
  // qtd_linhas_telemetria - NOVOS
  'quantidadedelinhastelemetria': 'qtd_linhas_telemetria',
  'qtdlinhastelemetria': 'qtd_linhas_telemetria',
  'linhastelemetria': 'qtd_linhas_telemetria',
  'qtdelinhastelemetria': 'qtd_linhas_telemetria',
  // classificacao
  'classificacao': 'classificacao',
  // telefone
  'telefone': 'telefone',
  'telefoneprincipal': 'telefone',
  'tel': 'telefone',
  'fone': 'telefone',
  'celular': 'telefone',
  'telefone1': 'telefone',
  'telefonedecontato': 'telefone',
  // telefones adicionais (TT CPF→CNPJ)
  'telefone2': 'telefone_2',
  'telefone3': 'telefone_3',
  'telefone4': 'telefone_4',
  // socio_adm / nome_adm / telefone_adm
  'nomeadm': 'nome_adm',
  'socioadm': 'nome_adm',
  'administrador': 'nome_adm',
  'adm': 'nome_adm',
  'contatoadm': 'nome_adm',
  'nomeadministrador': 'nome_adm',
  'nomedoadm': 'nome_adm',
  'telefoneadm': 'telefone_adm',
  'telefoneprocurador': 'telefone_adm',
  // valor_fatura_atual
  'valoratual': 'valor_fatura_atual',
  'valorfaturaatual': 'valor_fatura_atual',
  'valoatual': 'valor_fatura_atual',
  // cnpj (campo obrigatório)
  'cnpj': 'cnpj',
  'cnpjempresa': 'cnpj',
  // TT CPF→CNPJ: Campos específicos
  'cpfcnpjdocliente': 'doc_cpf',
  'cpfcnpj': 'doc_cpf',
  'nome': 'nome_proprietario',
  'razaosocial': 'razao_social',
  'nomefantasia': 'nome_fantasia',
  'endereco': 'endereco_empresa',
  'rua': 'endereco_empresa',
  'logradouro': 'endereco_empresa',
  // "end", "ENDEREÇO COMPLETO" e "END COMPLETO" trazem o endereço inteiro numa
  // string só; quando a planilha tem também RUA/ENDEREÇO, essa ganha (vem antes
  // no MAPEAMENTO_FORCADO só se estiver preenchida — a regra de não sobrescrever)
  'end': 'endereco_empresa',
  'enderecocompleto': 'endereco_empresa',
  'endcompleto': 'endereco_empresa',
  'estado': 'uf',
  // "CID" é a cidade da empresa nas bases de março
  'cid': 'cidade_pj',
  'complemento': 'complemento',
  'movel': 'valor_movel',
  // Planilha TT em inglês: Mobile / Combo / residential são os valores da conta
  'mobile': 'valor_movel',
  'combo': 'valor_combo',
  'residencial': 'valor_residencial',
  'residential': 'valor_residencial',
  'datafatura': 'data_fatura',
  'dataalt': 'data_fatura',
  'plano': 'produto',
  'sms': 'sms_status',
  'movel1': 'movel_1',
  'movel2': 'movel_2',
  'movel3': 'movel_3',
  'movel4': 'movel_4',
  'movel5': 'movel_5',
  'movel6': 'movel_6',
  'novoendereco': 'novo_endereco',
  // O endereço novo (CEP: ... | RUA: ... | BAIRRO: ... | CIDADE: ...) muda de nome
  // a cada base: MUD END, MUD END., MUD ENDEREÇO, MUDANÇA DE ENDEREÇO, Endereço SQL
  'mudend': 'novo_endereco',
  'mudendereco': 'novo_endereco',
  'mudancaendereco': 'novo_endereco',
  'enderecosql': 'novo_endereco',
  'status': 'status_planilha',
  'mudancadeenderecoabertafeita': 'mudanca_endereco',
  'mudancadeendereco': 'mudanca_endereco',
  // Novos campos Claro
  'codigo': 'codigo_claro',
  'codigoclaro': 'codigo_claro',
  'descontoclaro': 'desconto_claro',
  'desconto': 'desconto_claro',
  // 🔵 TT CPF→CNPJ: Campos PJ novos
  'pj': 'cnpj',
  'socios': 'socios',
  'nomedossocios': 'socios',
  'nomesdossocios': 'socios',
  'quadrosocietario': 'socios',
  'situacaocadastral': 'situacao_cadastral_pj',
  'cnaeprincipal': 'cnae_principal',
  'simples': 'simples_nacional',
  'simplesnacional': 'simples_nacional',
  'naturezajuridica': 'natureza_juridica',
  'mei': 'mei',
  'datasituacaocadastral': 'data_situacao_cadastral',
  'porte': 'porte_empresa',
  'porteempresa': 'porte_empresa',
  'city': 'cidade_pj',
  'empresa': 'razao_social',
  // ✅ TT CPF CNPJ: "NOME DA EMPRESA" normalizado ultra-agressivo → razao_social
  'nomadaempresa': 'razao_social',
  'nomedaempresa': 'razao_social',
  'nomeempresa': 'razao_social',
  
  // ==========================================
  // 🟣 PORTABILIDADE_VIVO - Headers MAPA Vivo
  // ==========================================
  'nrcnpj': 'cnpj',
  'nmcliente': 'razao_social',
  'mconsulta': 'tempo_contrato_voz',
  'valoratualconsulta': 'valor_fatura_atual',
  'valorcontamovel': 'valor_fatura_atual',
  'valorconta': 'valor_fatura_atual',
  'linhasmoveisconsulta': 'qtd_linhas_voz',
  'dataconsulta': 'data_consulta',
  'dsendereco': 'endereco',
  'dscidade': 'cidade',
  'nrcep': 'cep',
  'nmcontato': 'nome_completo',
  'emailcontatoprincipal': 'email',
  'celularcontatoprincipal': 'celular',
  'tlfn1': 'telefone',
  'tlfn2': 'telefone_2',
  'tlfn3': 'telefone_3',
  'tlfn4': 'telefone_4',
  'tlfn5': 'telefone_5',
  'qtmovelterm': 'qtd_linhas_voz',
  'qtmovelpen': 'qt_movel_pen',
  'qtbasicatermfibra': 'qt_basica_term_fibra',
  'qtbasicatermmetalico': 'qt_basica_term_metalico',
  'qtblftth': 'qt_bl_ftth',
  'qtblfttc': 'qt_bl_fttc',
  'vertical': 'vertical',
  'primeiraoferta': 'primeira_oferta',
  'recomendacao': 'recomendacao',
  'dscnae': 'cnae',
  'dsatividadeeconomica': 'atividade_economica',
  'tproduto': 'produto',
  'grupohierarquia': 'grupo_hierarquia',
  'hierarquia': 'grupo_hierarquia',
  'regional': 'regional',

  // ==========================================
  // 🟣 MAPA Vivo — colunas dinâmicas "MES EM [MES][ANO]" → tempo_contrato_voz
  // ==========================================
  // 2025
  'mesemjan25': 'tempo_contrato_voz',
  'mesemfev25': 'tempo_contrato_voz',
  'mesemmar25': 'tempo_contrato_voz',
  'mesemabr25': 'tempo_contrato_voz',
  'mesemmai25': 'tempo_contrato_voz',
  'mesemjun25': 'tempo_contrato_voz',
  'mesemjul25': 'tempo_contrato_voz',
  'mesemago25': 'tempo_contrato_voz',
  'mesemset25': 'tempo_contrato_voz',
  'mesemout25': 'tempo_contrato_voz',
  'mesemnov25': 'tempo_contrato_voz',
  'mesemdez25': 'tempo_contrato_voz',
  // 2026
  'mesemjan26': 'tempo_contrato_voz',
  'mesemfev26': 'tempo_contrato_voz',
  'mesemmar26': 'tempo_contrato_voz',
  'mesemabr26': 'tempo_contrato_voz',
  'mesemmai26': 'tempo_contrato_voz',
  'mesemjun26': 'tempo_contrato_voz',
  'mesemjul26': 'tempo_contrato_voz',
  'mesemago26': 'tempo_contrato_voz',
  'mesemset26': 'tempo_contrato_voz',
  'mesemout26': 'tempo_contrato_voz',
  'mesemnov26': 'tempo_contrato_voz',
  'mesemdez26': 'tempo_contrato_voz',
  // 2027
  'mesemjan27': 'tempo_contrato_voz',
  'mesemfev27': 'tempo_contrato_voz',
  'mesemmar27': 'tempo_contrato_voz',
  'mesemabr27': 'tempo_contrato_voz',
  'mesemmai27': 'tempo_contrato_voz',
  'mesemjun27': 'tempo_contrato_voz',
  'mesemjul27': 'tempo_contrato_voz',
  'mesemago27': 'tempo_contrato_voz',
  'mesemset27': 'tempo_contrato_voz',
  'mesemout27': 'tempo_contrato_voz',
  'mesemnov27': 'tempo_contrato_voz',
  'mesemdez27': 'tempo_contrato_voz',

  // ==========================================
  // 🔵 Fixa — qtd_linhas_fixas
  // ==========================================
  'qtdlinhasfixas': 'qtd_linhas_fixas',
  'quantidadedelinhasfixas': 'qtd_linhas_fixas',
  'linhasfixas': 'qtd_linhas_fixas',
  'qtdelinhasfixas': 'qtd_linhas_fixas',
  'linhasfixa': 'qtd_linhas_fixas',
  'qtdfixa': 'qtd_linhas_fixas',
  'linhasfixo': 'qtd_linhas_fixas',

  // ==========================================
  // 🔵 Fixa — valor_fatura_fixa
  // ==========================================
  'valorfaturafixa': 'valor_fatura_fixa',
  'valorfixa': 'valor_fatura_fixa',
  'faturafixa': 'valor_fatura_fixa',
  'valorcontafixa': 'valor_fatura_fixa',
  'vlfixa': 'valor_fatura_fixa',
  'contafixa': 'valor_fatura_fixa',
  'valorfixo': 'valor_fatura_fixa',
};

/**
 * Mapeia as colunas da planilha para os campos do banco
 * ✅ USA BUSCA FUZZY CASE-INSENSITIVE + DEBUG MASSIVO
 */
// 🔥 Contador global para log apenas na primeira linha
let logCounter = 0;

/**
 * OI — leitura por NOME EXATO das 44 colunas do fornecedor.
 *
 * As tres regras que definem o resultado, medidas na remessa de 09/09/2026
 * (5.054 linhas / 5.008 clientes):
 *
 * 1. QUEM E' O LEAD. A coluna `CPF` guarda o documento do titular na Oi — CPF
 *    quando tipo_pessoa='PF' (4.632) e CNPJ quando e' 'PJ' (422, e nesses casos
 *    ela repete a coluna CNPJ, conferido nos 422). A coluna `CNPJ` e' o bloco da
 *    Receita e aparece tambem em 1.604 linhas PF: sao pessoas donas de empresa
 *    (868 MEI, 1.233 Empresario Individual). Regra: TEM CNPJ -> lead PJ pelo
 *    CNPJ, porque e' o CNPJ que a Prime vende; o CPF fica em doc_cpf. Sem CNPJ
 *    nenhum -> lead PF pelo CPF. Da' 2.005 PJ + 3.003 PF.
 *
 * 2. ZERO A ESQUERDA. 2.113 das 5.054 linhas (42%) chegam com o documento
 *    encurtado pelo Excel. Como tipo_pessoa diz se sao 11 ou 14 digitos, o
 *    padStart e' determinado — e fecha o digito verificador em 5.054 de 5.054.
 *
 * 3. TELEFONE. Sao cinco colunas para 1 a 3 numeros de verdade: 'TELEFONE 1' e
 *    'TELEFONE 2' sao identicos em 87% das linhas e 'TELEFONE 3' e' o mesmo sem
 *    o 55. Deduplicamos pelos digitos, sem o 55, e jogamos fora repeticao obvia
 *    ('0', '00000000', '21999999999').
 *
 * 4. LAYOUT DE 01/10/2026. A Oi trocou o arquivo: VALOR_FATURA, NOME, RUA (o
 *    endereco inteiro numa coluna), BAIRRO, COMPLEMENTO ("2 CA", "BL B/AP 3"),
 *    TELEFONE 4 e TELEFONE. Saiu tipo_pessoa e todo o bloco do bloqueio. Sem
 *    tipo_pessoa, o tamanho do documento decide: mais de 11 digitos e' CNPJ.
 */
const OI_TIPO_IMOVEL: Record<string, string> = {
  CA: 'CASA', CASA: 'CASA', AP: 'APARTAMENTO', APTO: 'APARTAMENTO', APARTAMENTO: 'APARTAMENTO',
  BL: 'BLOCO', BLOCO: 'BLOCO', SB: 'SOBRADO', SOBRADO: 'SOBRADO', FU: 'FUNDOS', FUNDO: 'FUNDOS', FUNDOS: 'FUNDOS',
  LJ: 'LOJA', LOJA: 'LOJA', SL: 'SALA', SALA: 'SALA',
};

export function mapearOi(row: Record<string, unknown>): Record<string, unknown> {
  const col = (nome: string): string => {
    const v = row[nome];
    if (v === null || v === undefined) return '';
    return consertarMojibake(String(v)).trim();
  };

  const docTitular = col('CPF');
  const tipoArquivo = col('tipo_pessoa').toUpperCase();
  const tipoPessoaOi = tipoArquivo
    ? (tipoArquivo === 'PJ' ? 'PJ' : 'PF')
    : (docTitular.replace(/\D/g, '').length > 11 ? 'PJ' : 'PF');
  const cnpjReceita = normalizarCNPJPlanilha(col('CNPJ'));
  // Em linha PJ a coluna CPF repete o CNPJ; vale como segunda chance se o bloco
  // da Receita vier vazio.
  const cnpjTitular = tipoPessoaOi === 'PJ' ? normalizarCNPJPlanilha(docTitular) : null;
  const cnpj = cnpjReceita || cnpjTitular;
  const cpfTitular = tipoPessoaOi === 'PF' ? normalizarCPFPlanilha(docTitular) : null;
  const cpfValido = cpfTitular && cpfTemDVValido(cpfTitular) ? cpfTitular : null;

  const nomeCliente = col('nome_cliente') || col('NOME');
  const razaoReceita = col('RAZAO SOCIAL');

  const LIXO = new Set(['0', '00000000', '000000000', '0000000000', '00000000000', '21999999999']);
  const telefones: string[] = [];
  for (const nome of ['TELEFONE 1', 'TELEFONE 2', 'TELEFONE 3', 'TELEFONE 4', 'TELEFONE', 'TELEFONE1', 'TELEFONE2']) {
    let d = col(nome).replace(/\D/g, '');
    if (d.startsWith('55') && d.length >= 12) d = d.slice(2);
    if (d.length < 10 || d.length > 11) continue;
    if (LIXO.has(d) || new Set(d).size <= 2) continue;
    if (!telefones.includes(d)) telefones.push(d);
  }

  const endereco = [col('tipo_logradouro_inst'), col('nome_logradouro_inst')]
    .filter(Boolean).join(' ').trim() || col('RUA').split(/,|\sCEP:/)[0].trim();
  // NUMERO = '0' em 790 linhas: e' "sem numero", nao o numero zero.
  const numero = col('NUMERO') === '0' ? '' : col('NUMERO');
  const complemento = [
    col('tipo_complemento_instalacao'),
    col('numero_complemento_1_inst'),
    col('numero_complemento_2_inst'),
    col('numero_complemento_3_inst'),
  ].filter(Boolean).join(' ').trim()
    || col('COMPLEMENTO').replace(/^Complementos:/i, '').replace(/\s+/g, ' ').trim();
  const tipoImovel = col('tipo_complemento_instalacao')
    || complemento.split(' ').map(t => OI_TIPO_IMOVEL[t.toUpperCase()]).find(Boolean) || '';
  const bairro = col('bairro_instalacao') || col('BAIRRO');
  const cep = normalizarCEPPlanilha(col('CEP'), col('UF'));

  return {
    tipo_lead: 'OI',
    tipo_pessoa: cnpj ? 'PJ' : 'PF',
    cnpj: cnpj || null,
    cpf: cnpj ? null : cpfValido,
    // O CPF do titular fica registrado nos dois casos: e' ele que a Busca 360
    // indexa, e em lead PJ e' o CPF do dono da empresa.
    doc_cpf: cpfValido || (tipoPessoaOi === 'PF' ? cpfTitular : null),
    razao_social: razaoReceita || nomeCliente || 'NOME NAO INFORMADO',
    nome_proprietario: nomeCliente || null,
    titular_tipo_pessoa: tipoPessoaOi,
    telefone_titular: ['TELEFONE 1', 'TELEFONE 2', 'TELEFONE 3', 'TELEFONE 4'].map(k => limparTelefonePlanilha(row[k])).find(t => t && telefones.includes(t)) || null,
    nome_adm: nomeCliente || null,
    socios: col('NOME DOS SOCIOS') || null,
    email: col('EMAIL_RFB') || null,

    telefone: telefones[0] || null,
    telefone_2: telefones[1] || null,
    telefone_3: telefones[2] || null,
    telefone_4: telefones[3] || null,
    ddd: telefones[0] ? telefones[0].slice(0, 2) : null,

    // ── o que ele tem na Oi hoje: e' o motivo da ligacao ──
    operadora_atual: 'OI',
    conta_operadora: col('id_conta') || null,
    valor_fatura_atual: parseValorPlanilha(col('VALOR FATURA') || col('VALOR_FATURA')),
    plano_atual: col('linha_produto') || null,
    produto: col('classe_produto') || null,
    tipo_produto: col('tipo_produto') || null,
    segmento: col('grupo_unidade') || null,
    oi_acesso_gpon: col('acesso_gpon') || col('ACESSO_GPON') || null,
    oi_estacao: col('codigo_estacao') || null,
    oi_motivo_bloqueio: col('ds_servico') || null,
    oi_tipo_bloqueio: col('ds_tipo_servico') || null,
    oi_status_bloqueio: col('statusbloq') || null,
    oi_data_bloqueio: formatarDataPlanilha(col('data_do_pedido')),

    // ── onde o servico esta instalado ──
    // A lista NAO traz o endereco cadastral da empresa, so' o da instalacao.
    // Por isso uf/cidade do lead sao os da instalacao — e' o unico endereco que
    // existe aqui, diferente da Algar, que manda os dois.
    uf: col('UF') || null,
    cidade: col('CIDADE') || null,
    cep,
    endereco_empresa: endereco || null,
    numero_endereco: numero || null,
    bairro: bairro || null,
    venda_logradouro: endereco || null,
    venda_numero: numero || null,
    venda_complemento: complemento || null,
    venda_bairro: bairro || null,
    venda_cidade: col('CIDADE') || null,
    venda_uf: col('UF') || null,
    venda_cep: cep,
    // A Oi diz com todas as letras o que e' o imovel da instalacao: CASA (1.968),
    // APARTAMENTO (389), LOTE, LOJA, SALA... Nao ha' regex a adivinhar. Quando e'
    // moradia e o cliente tem empresa, a fibra esta na casa dele — e sobra a
    // empresa para vender (Douglas, 09/09/2026).
    venda_residencial: /^(CASA|APARTAMENTO|APTO|AP|BLOCO|BL|CONDOMINIO|COND|SOBRADO|CHACARA|SITIO|FUNDOS)$/i
      .test(tipoImovel),
    oi_tipo_complemento: tipoImovel || null,

    // ── o carimbo que decide se ha' o que vender ──
    // Vem do fornecedor. A nossa consulta (cep_cobertura_claro_bl) e' consultada
    // na tela do lead, como em todo lead do CRM — as duas convivem.
    cobertura_fibra: col('COBERTURA') || null,

    // ── bloco da Receita, so' existe quando ha' CNPJ ──
    situacao_cadastral: col('SITUACAO_CADASTRAL') || null,
    cnae_principal: col('CNAE PRINCIPAL') || null,
    simples: col('SIMPLES') || null,
    natureza_juridica: col('NATUREZA JURIDICA') || null,
    mei: col('MEI') || null,
    porte: col('PORTE') || null,
  };
}

export function mapearAlgarAtual(row: Record<string, unknown>): Record<string, unknown> {
  const col = (nome: string) => String(row[nome] ?? '').trim();
  const documento = col('Documento').replace(/\D/g, '');
  const cnpjReceita = normalizarCNPJPlanilha(col('CNPJ'));
  const cnpjTitular = documento.length > 11 ? normalizarCNPJPlanilha(documento) : null;
  const cnpj = [cnpjReceita, cnpjTitular].find(d => d && cnpjTemDVValido(d)) || null;
  const cpfTitular = documento.length <= 11 ? normalizarCPFPlanilha(documento) : null;
  const cpf = cpfTitular && cpfTemDVValido(cpfTitular) ? cpfTitular : null;
  const telefones = [...new Set(['Telefone', 'TELEFONE1', 'TELEFONE2'].map(k => limparTelefonePlanilha(row[k])).filter(Boolean))];
  return {
    tipo_lead: 'ALGAR', tipo_pessoa: cnpj ? 'PJ' : 'PF', cnpj, cpf: cnpj ? null : cpf,
    doc_cpf: cpf, nome_proprietario: col('Cliente') || null,
    telefone_titular: limparTelefonePlanilha(row['Telefone']) || null,
    contato: col('Contato') || null, email_atendimento: col('Email') || null,
    email_receita: col('EMAIL_RFB') || null, empresa_telefone: limparTelefonePlanilha(row['TELEFONE1']) || null,
    empresa_telefone_2: limparTelefonePlanilha(row['TELEFONE2']) || null,
    simples: col('SIMPLES') || null, natureza_juridica: col('NATUREZA JURIDICA') || null, porte: col('PORTE') || null,
    razao_social: (cnpj ? col('RAZAO SOCIAL') : '') || col('Cliente') || null,
    socios: col('NOME DOS SOCIOS') || null,
    telefone: telefones[0] || null, telefone_2: telefones[1] || null, telefone_3: telefones[2] || null,
    email: col('Email') || col('EMAIL_RFB') || null,
    endereco_empresa: col('Endereco') || null, numero_endereco: col('Numero') || null,
    cep: normalizarCEPPlanilha(col('CEP'), col('UF')), cidade: col('Cidade') || null, uf: col('UF') || null,
    cobertura_fibra: col('COBERTURA') || null, mei: col('MEI') || null,
    cnae_principal: col('CNAE PRINCIPAL') || null, situacao_cadastral: col('SITUACAO_CADASTRAL') || null,
    produto: col('Produto') || null, tipo_produto: col('Tipo Produto') || null,
    algar_status: col('Status') || null, algar_substatus: col('SubStatus') || null,
    algar_criacao: col('Criacao') || null, algar_canal: col('Canal De Venda') || null,
    algar_motivos: ['Motivo1', 'Motivo2', 'Motivo3'].map(col).filter(Boolean),
    algar_layout_atual: true,
  };
}

export function mapearColunas(row: Record<string, any>, tipoLead: string | null): Record<string, any> {
  const resultado: Record<string, any> = {
    tipo_lead: tipoLead
  };
  
  // OI nao passa pelo mapeamento fuzzy: layout fixo, lido por nome exato.
  if (tipoLead === 'OI') return mapearOi(row);
  if (tipoLead === 'ALGAR' && Object.prototype.hasOwnProperty.call(row, 'Documento')) return mapearAlgarAtual(row);

  const colunas = Object.keys(row);
  const isFirstRow = logCounter === 0;
  logCounter++;
  
  // 🔥 DEBUG ULTRA-DETALHADO: Mostrar TODAS as colunas e seus valores (apenas primeira linha)
  if (isFirstRow) {
    console.log('\n🔥🔥🔥======== DIAGNÓSTICO COMPLETO DE COLUNAS (FRONTEND) ========🔥🔥🔥');
    console.log(`📊 Total de colunas na planilha: ${colunas.length}`);
    
    for (const col of colunas) {
      const colUltra = normalizarUltraAgressivo(col);
      const valor = row[col];
      // ⚠️ Destacar colunas que contêm palavras-chave importantes
      const isTempo = colUltra.includes('tempo') || colUltra.includes('contrato') || colUltra.includes('voz');
      const prefix = isTempo ? '⚠️⚠️⚠️' : '   ';
      console.log(`${prefix} COL[${colunas.indexOf(col)}]: "${col}" → ULTRA: "${colUltra}" → VALOR: "${valor}"`);
    }
    console.log('🔥🔥🔥=============================================================🔥🔥🔥\n');
  }
  
  if (!tipoLead || !TIPOS_LEAD_CONFIG[tipoLead]) {
    // Mapeamento genérico
    Object.entries(row).forEach(([key, value]) => {
      const keyNorm = key.toLowerCase().replace(/\s+/g, '_');
      resultado[keyNorm] = value;
    });
    return resultado;
  }
  
  const config = TIPOS_LEAD_CONFIG[tipoLead];
  
  // 🆕 ESTRATÉGIA 0: Mapeamento forçado por normalização ultra-agressiva
  for (const col of colunas) {
    const colUltra = normalizarUltraAgressivo(col);
    if (MAPEAMENTO_FORCADO[colUltra]) {
      const campoDb = MAPEAMENTO_FORCADO[colUltra];
      // Nao sobrescrever se o campo ja foi preenchido por uma coluna mais especifica
      if (resultado[campoDb] === undefined || resultado[campoDb] === null || resultado[campoDb] === '') {
        resultado[campoDb] = row[col];
      }
      if (isFirstRow && campoDb === 'tempo_contrato_voz') {
        console.log(`✅ [FORÇADO] tempo_contrato_voz: "${col}" (ultra: "${colUltra}") → valor: "${row[col]}"`);
      }
    }
  }

  // 🔵 TT CPF→CNPJ: as colunas depois do CNPJ são o bloco da EMPRESA.
  // CIDADE/TELEFONE1/TELEFONE2 repetem o nome das colunas da PF e eram engolidas
  // pelo mapeamento (a PF vem primeiro e ganha) — aqui elas viram os campos PJ.
  if (tipoLead === 'TT_CPF_CNPJ') {
    const idxCnpj = colunas.findIndex(c => {
      const u = normalizarUltraAgressivo(c);
      return u === 'cnpj' || u === 'pj' || u === 'cnpjempresa';
    });
    if (idxCnpj >= 0) {
      // Antes do CNPJ = bloco da PESSOA FÍSICA (titular da conta).
      // CIDADE aparece dos dois lados e o alias exato pegava a da empresa.
      for (let i = 0; i < idxCnpj; i++) {
        const u = normalizarUltraAgressivo(colunas[i]);
        const val = row[colunas[i]];
        if (u === 'cidade' && val !== undefined && val !== null && String(val).trim() !== '' && !resultado.cidade) {
          resultado.cidade = val;
        }
      }

      let ordemTelPj = 0;
      for (let i = idxCnpj + 1; i < colunas.length; i++) {
        const u = normalizarUltraAgressivo(colunas[i]);
        const val = row[colunas[i]];
        const temValor = val !== undefined && val !== null && String(val).trim() !== '';

        if (u === 'cidade' && temValor && !resultado.cidade_pj) {
          resultado.cidade_pj = val;
          continue;
        }

        const mTel = u.match(/^telefone(\d?)$/);
        if (mTel) {
          const pos = mTel[1] ? parseInt(mTel[1], 10) : ++ordemTelPj;
          if (pos >= 1 && pos <= 2 && temValor && !resultado[`telefone_pj_${pos}`]) {
            resultado[`telefone_pj_${pos}`] = val;
          }
        }
      }
    }
  }

  // 🔵 TT CPF→CNPJ: SEMPRE contar Movel 1..18 preenchidos (esses campos SÃO os números das linhas)
  if (tipoLead === 'TT_CPF_CNPJ') {
    let qtdContada = 0;
    for (let i = 1; i <= MAX_LINHAS_MOVEL; i++) {
      const preenchido = [`Movel ${i}`, `MOVEL_${i}`, `movel_${i}`, `MOVEL${i}`, `Móvel ${i}`]
        .some(c => row[c] !== undefined && row[c] !== null && String(row[c]).trim() !== '');
      if (preenchido) qtdContada++;
    }
    if (qtdContada > 0) {
      resultado.qtd_linhas_voz = qtdContada;
    }
  }

  // 🔥 ESTRATÉGIA 0.5: Busca por "CONTÉM" para tempo_contrato_voz (caso não encontrado ainda)
  if (resultado.tempo_contrato_voz === undefined) {
    for (const col of colunas) {
      const colUltra = normalizarUltraAgressivo(col);
      // Se contém "tempo" + "voz" OU "contrato" + "voz", provavelmente é o campo certo
      const contemTempoVoz = colUltra.includes('tempo') && colUltra.includes('voz');
      const contemContratoVoz = colUltra.includes('contrato') && colUltra.includes('voz');
      // ⚠️ Excluir "quantidade" ou "linhas" para não confundir com qtd_linhas_voz
      const ehQuantidade = colUltra.includes('quantidade') || colUltra.includes('linhas') || colUltra.includes('qtd');
      
      if ((contemTempoVoz || contemContratoVoz) && !ehQuantidade) {
        resultado.tempo_contrato_voz = row[col];
        if (isFirstRow) {
          console.log(`✅ [CONTÉM] tempo_contrato_voz: "${col}" (ultra: "${colUltra}") → valor: "${row[col]}"`);
        }
        break;
      }
    }
  }

  // 🆕 ESTRATÉGIA 0.6: Regex MAPA Vivo — padrão "MES EM [MES][ANO]" → tempo_contrato_voz
  // Captura qualquer variante futura (MES EM FEV27, MES EM MAR28, etc.) sem precisar de alias manual
  if (resultado.tempo_contrato_voz === undefined || resultado.tempo_contrato_voz === null || resultado.tempo_contrato_voz === '') {
    for (const col of colunas) {
      if (/^mes\s+em\s+[a-z]{3}\d{2}$/i.test(col.trim())) {
        const val = row[col];
        if (val !== undefined && val !== null && val !== '') {
          resultado.tempo_contrato_voz = val;
          if (isFirstRow) {
            console.log(`✅ [REGEX MES EM] tempo_contrato_voz: "${col}" → valor: "${val}"`);
          }
          break;
        }
      }
    }
  }
  
  // Para cada campo do mapeamento, tentar encontrar o valor usando 3 estratégias:
  for (const [campoDb, aliases] of Object.entries(config.mapeamento)) {
    // Se já foi encontrado pelo mapeamento forçado, pular
    if (resultado[campoDb] !== undefined) {
      continue;
    }
    
    let valorEncontrado = false;
    
    // 1️⃣ BUSCA EXATA: match direto pelo nome da coluna
    for (const alias of aliases) {
      if (row[alias] !== undefined) {
        resultado[campoDb] = row[alias];
        valorEncontrado = true;
        if (campoDb === 'tempo_contrato_voz') {
          console.log(`✅ EXATO tempo_contrato_voz: alias="${alias}" → valor="${row[alias]}"`);
        }
        break;
      }
    }
    
    // 2️⃣ BUSCA FUZZY: se não encontrou, buscar com normalização
    if (!valorEncontrado) {
      for (const colunaPlanilha of colunas) {
        if (buscarColunaFuzzy(colunaPlanilha, aliases)) {
          resultado[campoDb] = row[colunaPlanilha];
          console.log(`🔍 FUZZY MATCH: "${colunaPlanilha}" → ${campoDb} = "${row[colunaPlanilha]}"`);
          valorEncontrado = true;
          if (campoDb === 'tempo_contrato_voz') {
            console.log(`✅ FUZZY tempo_contrato_voz: coluna="${colunaPlanilha}" → valor="${row[colunaPlanilha]}"`);
          }
          break;
        }
      }
    }
    
    // 3️⃣ BUSCA ULTRA-AGRESSIVA: última tentativa
    if (!valorEncontrado) {
      for (const colunaPlanilha of colunas) {
        if (buscarColunaUltraAgressivo(colunaPlanilha, aliases)) {
          resultado[campoDb] = row[colunaPlanilha];
          console.log(`🔥 ULTRA MATCH: "${colunaPlanilha}" → ${campoDb} = "${row[colunaPlanilha]}"`);
          valorEncontrado = true;
          if (campoDb === 'tempo_contrato_voz') {
            console.log(`✅ ULTRA tempo_contrato_voz: coluna="${colunaPlanilha}" → valor="${row[colunaPlanilha]}"`);
          }
          break;
        }
      }
    }
  }
  
  // 🔥 DEBUG FINAL: Mostrar resultado de tempo_contrato_voz
  console.log(`📊 RESULTADO tempo_contrato_voz: "${resultado.tempo_contrato_voz}" (tipo: ${typeof resultado.tempo_contrato_voz})`);
  
  // 🆕 Scan posicional: capturar telefones dos sócios (colunas TELEFONE1/2 após SOCIOS)
  if (tipoLead === 'TT_CPF_CNPJ') {
    let sociosColIndex = -1;
    for (let i = 0; i < colunas.length; i++) {
      const ultra = normalizarUltraAgressivo(colunas[i]);
      if (ultra === 'socios') {
        sociosColIndex = i;
        break;
      }
    }
    if (sociosColIndex >= 0) {
      let telCount = 0;
      for (let j = sociosColIndex + 1; j < colunas.length && telCount < 2; j++) {
        const ultra = normalizarUltraAgressivo(colunas[j]);
        if (ultra.includes('telefone') || ultra.includes('tel') || ultra.includes('fone')) {
          const val = row[colunas[j]];
          if (val && String(val).trim()) {
            telCount++;
            resultado[`telefone${telCount}_socio`] = String(val).trim();
          }
        } else {
          break;
        }
      }
    }
  }

  // Normalizar CNPJ (só dígitos, trunca acima de 14, recompõe zero à esquerda do Excel).
  // Sem CNPJ válido o campo fica nulo — e a linha é barrada em validarLinha.
  if (resultado.cnpj) {
    resultado.cnpj = normalizarCNPJPlanilha(resultado.cnpj);
  }

  // Normalizar CPF (repõe o zero à esquerda que o Excel come, com guarda de DV)
  if (resultado.doc_cpf) {
    resultado.doc_cpf = normalizarCPFPlanilha(resultado.doc_cpf);
  }

  // Normalizar CEP (8 dígitos — sem isso o CEP de SP perde o zero da frente)
  if (resultado.cep) {
    resultado.cep = normalizarCEPPlanilha(resultado.cep, resultado.uf);
  }

  // Normalizar TODOS os telefones: a planilha traz " 18  99731 9737" / "  4  13383 3627".
  // Só dígitos, e o que não é telefone com DDD (10/11 dígitos) é descartado.
  for (const campoTel of [
    'telefone', 'telefone_2', 'telefone_3', 'telefone_4', 'telefone_5',
    'telefone_pj_1', 'telefone_pj_2', 'telefone_adm',
    'telefone1_socio', 'telefone2_socio',
  ]) {
    if (resultado[campoTel] !== undefined && resultado[campoTel] !== null && resultado[campoTel] !== '') {
      resultado[campoTel] = limparTelefonePlanilha(resultado[campoTel]);
    }
  }

  // Linhas móveis (Movel 1..18): limpa o número, mas nunca descarta o conteúdo original
  for (let i = 1; i <= MAX_LINHAS_MOVEL; i++) {
    const campoMovel = `movel_${i}`;
    const bruto = resultado[campoMovel];
    if (bruto !== undefined && bruto !== null && String(bruto).trim() !== '') {
      resultado[campoMovel] = limparTelefonePlanilha(bruto) ?? String(bruto).trim();
    }
  }

  // Converter números
  const camposNumericos = ['tempo_contrato_voz', 'tempo_contrato_bl', 'tempo_contrato_telemetria', 'qtd_linhas_voz', 'qtd_linhas_bl', 'qtd_linhas_telemetria', 'linhas_cpf'];
  for (const campo of camposNumericos) {
    if (resultado[campo] !== undefined && resultado[campo] !== null && resultado[campo] !== '') {
      const valorOriginal = resultado[campo];
      const num = parseFloat(String(resultado[campo]).replace(',', '.'));
      resultado[campo] = isNaN(num) ? null : num;
      if (campo === 'tempo_contrato_voz') {
        console.log(`📊 CONVERSÃO tempo_contrato_voz: "${valorOriginal}" → ${resultado[campo]} (tipo: ${typeof resultado[campo]})`);
      }
    }
  }
  
  // 🔥 Converter valor_fatura_atual (moeda BR: "R$ 2.118,64" → 2118.64)
  if (resultado.valor_fatura_atual !== undefined && resultado.valor_fatura_atual !== null && resultado.valor_fatura_atual !== '') {
    const valorOriginal = resultado.valor_fatura_atual;
    let valorStr = String(valorOriginal);
    
    // Remover símbolo de moeda e espaços
    valorStr = valorStr.replace(/R\$\s*/gi, '').trim();
    
    // Detectar formato BR (1.234,56) vs formato US (1,234.56)
    // BR: ponto como separador de milhar, vírgula como decimal
    // US: vírgula como separador de milhar, ponto como decimal
    const temVirgula = valorStr.includes(',');
    const temPonto = valorStr.includes('.');
    
    let valorNumerico: number;
    
    if (temVirgula && temPonto) {
      // Formato BR: "2.118,64" → remover pontos, trocar vírgula por ponto
      valorStr = valorStr.replace(/\./g, '').replace(',', '.');
    } else if (temVirgula && !temPonto) {
      // Apenas vírgula: "2118,64" → trocar vírgula por ponto
      valorStr = valorStr.replace(',', '.');
    }
    // Se só tem ponto ou nenhum separador, já está no formato correto
    
    valorNumerico = parseFloat(valorStr);
    resultado.valor_fatura_atual = isNaN(valorNumerico) ? null : valorNumerico;
    
    console.log(`💰 CONVERSÃO valor_fatura_atual: "${valorOriginal}" → ${resultado.valor_fatura_atual}`);
  }

  // 🔥 Converter valor_fatura_fixa (mesmo padrão moeda BR)
  if (resultado.valor_fatura_fixa !== undefined && resultado.valor_fatura_fixa !== null && resultado.valor_fatura_fixa !== '') {
    const valorOriginal = resultado.valor_fatura_fixa;
    let valorStr = String(valorOriginal).replace(/R\$\s*/gi, '').trim();
    const temVirgula = valorStr.includes(',');
    const temPonto = valorStr.includes('.');
    if (temVirgula && temPonto) {
      valorStr = valorStr.replace(/\./g, '').replace(',', '.');
    } else if (temVirgula && !temPonto) {
      valorStr = valorStr.replace(',', '.');
    }
    const valorNumerico = parseFloat(valorStr);
    resultado.valor_fatura_fixa = isNaN(valorNumerico) ? null : valorNumerico;
    console.log(`💰 CONVERSÃO valor_fatura_fixa: "${valorOriginal}" → ${resultado.valor_fatura_fixa}`);
  }

  // Converter qtd_linhas_fixas para inteiro
  if (resultado.qtd_linhas_fixas !== undefined && resultado.qtd_linhas_fixas !== null && resultado.qtd_linhas_fixas !== '') {
    const num = parseInt(String(resultado.qtd_linhas_fixas).replace(/\D/g, ''), 10);
    resultado.qtd_linhas_fixas = isNaN(num) ? null : num;
  }

  // Converter datas
  if (resultado.data_limite_renovacao) {
    resultado.data_limite_renovacao = parseData(resultado.data_limite_renovacao);
  }

  // Data de situação cadastral vem como serial do Excel (39653) ou ISO → DD/MM/AAAA
  if (resultado.data_situacao_cadastral) {
    resultado.data_situacao_cadastral = formatarDataPlanilha(resultado.data_situacao_cadastral);
  }

  // 🔵 TT CPF→CNPJ: fechar os dados que a tela do lead usa
  if (tipoLead === 'TT_CPF_CNPJ') {
    // A coluna do endereço novo muda de nome a cada base (MUD END, MUD ENDEREÇO,
    // MUDANÇA DE ENDEREÇO, Endereço SQL). Decidimos pelo CONTEÚDO, não pelo nome:
    // "Sim/Não" é bandeira; qualquer outro texto é o endereço em si.
    const ehFlag = (v: unknown) => /^(sim|s|nao|não|n|aberta|feita)$/i.test(String(v ?? '').trim());

    const mudBruto = String(resultado.mudanca_endereco ?? '').trim();
    if (mudBruto && !ehFlag(mudBruto) && !resultado.novo_endereco) {
      resultado.novo_endereco = mudBruto;
      resultado.mudanca_endereco = null;
    }

    const mudEnd = resultado.novo_endereco ? String(resultado.novo_endereco).trim() : '';
    if (mudEnd) {
      if (ehFlag(mudEnd)) {
        if (!resultado.mudanca_endereco) resultado.mudanca_endereco = mudEnd.toUpperCase();
        resultado.novo_endereco = null;
      } else if (!resultado.mudanca_endereco) {
        resultado.mudanca_endereco = 'SIM';
      }
    }

    // VALOR DA CONTA = Mobile + Combo + residential (a planilha usa ponto decimal)
    const vMovel = parseValorPlanilha(resultado.valor_movel) || 0;
    const vCombo = parseValorPlanilha(resultado.valor_combo) || 0;
    const vResid = parseValorPlanilha(resultado.valor_residencial) || 0;
    const total = vMovel + vCombo + vResid;
    if (total > 0 && !resultado.valor_fatura_atual) {
      resultado.valor_fatura_atual = Number(total.toFixed(2));
    }

    // Tipo do cliente: sem linha móvel e com valor residencial, é RESIDENCIAL
    if (!resultado.tipo_cliente) {
      if (vMovel > 0 && vResid > 0) resultado.tipo_cliente = 'COMBO';
      else if (vResid > 0) resultado.tipo_cliente = 'RESIDENCIAL';
      else if (vCombo > 0) resultado.tipo_cliente = 'COMBO';
      else if (vMovel > 0) resultado.tipo_cliente = 'MÓVEL';
    }
  }

  return resultado;
}

/**
 * VALIDAÇÃO: tolerante em tudo, MENOS no CNPJ.
 * Lead sem CNPJ não entra no CRM — não há como vincular carteira, pedido nem cliente.
 * O resto continua só gerando aviso.
 */
export function validarLinha(linha: Record<string, any>, _tipoLead: string | null): { valido: boolean; erros: string[]; avisos: string[] } {
  const avisos: string[] = [];
  const erros: string[] = [];

  // ⛔ REGRA: sem DOCUMENTO, não importa.
  // Ate 09/09/2026 documento era so' CNPJ. Com o lead PF (lista da Oi), a regra
  // vale igual — o que muda e' QUAL documento: CNPJ em lead PJ, CPF em lead PF.
  // Um lead nunca tem os dois: o banco recusa (leads_carteira_documento_por_tipo).
  const cnpj = linha.cnpj ? String(linha.cnpj).replace(/\D/g, '') : '';
  const cpf = linha.cpf ? String(linha.cpf).replace(/\D/g, '') : '';
  if (cnpj && cpf) {
    erros.push('Lead com CNPJ e CPF ao mesmo tempo');
  } else if (cnpj) {
    if (cnpj.length !== 14) erros.push(`CNPJ inválido (${cnpj.length} dígitos)`);
  } else if (cpf) {
    if (cpf.length !== 11) erros.push(`CPF inválido (${cpf.length} dígitos)`);
    else if (!cpfTemDVValido(cpf)) erros.push('CPF com dígito verificador errado');
  } else {
    erros.push('Sem CNPJ nem CPF');
  }

  // Avisar se razão social estiver ausente (mas NÃO rejeitar)
  if (!linha.razao_social && !linha.nome_fantasia) {
    avisos.push('Nome/Razão Social ausente');
  }

  return {
    valido: erros.length === 0,
    erros,
    avisos
  };
}

/**
 * Converte data em diversos formatos para ISO
 */
function parseData(valor: any): string | null {
  if (!valor) return null;
  
  const str = String(valor).trim();
  
  // Já é ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.substring(0, 10);
  }
  
  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split('/');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  // DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [d, m, y] = str.split('-');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  // Excel serial date
  if (/^\d+$/.test(str)) {
    const serial = parseInt(str);
    if (serial > 25000 && serial < 50000) {
      const date = new Date((serial - 25569) * 86400 * 1000);
      return date.toISOString().substring(0, 10);
    }
  }
  
  return null;
}
